import './MunicipalityCard.css';
import type { ComponentChildren } from 'preact';
import { useId } from 'preact/hooks';
import { Button } from '../../components/Button';
import { CandidateRow } from '../../components/CandidateRow';
import { Chip } from '../../components/Chip';
import { KeyValueRow } from '../../components/KeyValueRow';
import { SectionLabel } from '../../components/SectionLabel';
import { StateSeal } from '../../components/StateSeal';
import { t } from '../../copy/strings';
import { fmtPct, fmtSections, fmtVoters } from '../../format/numbers';
import { placeName, ufOf } from '../../format/places';
import { municipalityChip, municipalityFromColumns, municipalityFromDetail } from '../../model/municipality';
import { municipalTally2022, ref2022Of } from '../../model/ref2022';
import type { UfCode } from '../../model/types';
import { openPlace, openProfile } from '../../state/actions';
import {
  useConfig,
  useHeadline,
  useLayout,
  usePhase,
  useRouteState,
  useStatics,
  useTimeline,
  useView,
} from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import { PlaceCard } from './PlaceCard';
import { PlaceHeader } from './PlaceHeader';
import { UfNavButtons } from './UfNavButtons';
import { UpLink } from './UpLink';
import { useStateDetail } from './useStateDetail';

const pct1 = (v: number): string => fmtPct(v, 1);

/** Bandeira da UF (§2.7.9), a mesma do `UfCard`. */
const flagSrc = (uf: UfCode): string => `${import.meta.env.BASE_URL}flags/${uf.toLowerCase()}.webp`;

/**
 * Cartão do município no site ao vivo (§8.4): o presidente no município, com a nota de seções, as duas linhas com barra
 * e votos, a linha "2022", os indicadores (Comparecimento e Brancos e nulos; a Abstenção fica de fora, `docs/DECISOES.md`
 * §28) e "Ver o estado {uf_de}". Lê a rota (`place.kind === 'municipality'`), a vista
 * e os estáticos; o `StateDetail` da UF é pedido pela ação `stateDetail` ao abrir e a cada `seq` ao vivo (o `FeedClient`
 * deduplica e guarda em cache). Neutralidade: linhas na ordem do contrato (13 | 22), nunca por votos (regra inviolável
 * 2; `docs/DECISOES.md`, 10/10), mesmas classes para os dois lados.
 *
 * Fora do ao vivo (o site revendo a noite com `?t=`), o detalhe do instante não existe: a nota F81 com a fração do
 * eleitorado das colunas da vista, sem linhas nem indicadores (§8.4 "Replay sem detalhe do instante").
 */
export interface MunicipalityCardProps {
  /** O quadro de Situação incomum do município (app/insightsSlot.tsx, chunk `insights`), por último no cartão. */
  insights?: ComponentChildren;
}

export function MunicipalityCard({ insights = null }: MunicipalityCardProps = {}) {
  const route = useRouteState();
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const layout = useLayout();
  const phase = usePhase();
  const headline = useHeadline();
  const { live } = useTimeline();
  const id = useId();

  const place = route.place.kind === 'municipality' ? route.place : null;
  const uf = place?.uf ?? null;
  const tv = layout.mode === 'tv';
  const seq = view?.seq ?? null;

  // §8.4 "Dados": o detalhe da UF ao abrir e a cada seq novo ao vivo (useStateDetail)
  const detail = useStateDetail(uf, live, seq);

  if (place === null || uf === null) return null;

  const geo = statics.geography;
  const m = geo?.municipalities.find((x) => x.ibge === place.ibge) ?? null;
  const name = m?.name ?? place.ibge;
  const pair = view?.candidates ?? statics.candidates?.president ?? null;
  const photos = config.features.photos;
  const beforeOpen = phase === 'pre_open' || headline === 'countdown';
  const interactive = !tv;

  const mine = detail;
  const numbers = live && pair ? municipalityFromDetail(mine?.value, place.ibge, pair) : null;
  const cols = m ? municipalityFromColumns(view?.municipal, m.idx) : null;
  const chip = beforeOpen ? 'waiting' : municipalityChip(numbers);
  const hasRace =
    config.features.governors && (view ? view.governor[uf] != null : statics.candidates?.governor?.[uf] != null);

  /** Estado do cartão (`data-state`): a tabela de estados do §8.4. */
  const state = beforeOpen
    ? 'before_open'
    : !live
      ? 'replay'
      : numbers
        ? numbers.sectionsCounted > 0
          ? 'votes'
          : 'no_sections'
        : // o detalhe chegou sem o município (o contrato aceita um subconjunto de chaves): erro, nunca "carregando"
          mine?.status === 'error' || mine?.status === 'ok'
          ? 'error'
          : 'loading';

  // linhas: do detalhe; enquanto ele não chega, as fatias das colunas da vista (sem votos)
  const shares = numbers ? numbers.shares : state === 'loading' ? (cols?.shares ?? null) : null;
  const votes = numbers && numbers.valid > 0 ? numbers.votes : null;
  const showRows = (state === 'votes' || state === 'loading') && shares !== null;
  // §8.4 "Antes das 17h / sem seção": os nomes na ordem de número, sem valor (também com seções e nenhum voto válido)
  const showNames = !showRows && (state === 'before_open' || state === 'no_sections' || state === 'votes');
  const ref =
    state === 'votes' && pair && shares && m
      ? ref2022Of(statics.baseline, municipalTally2022(statics.baseline, m.idx), pair, shares)
      : null;

  const note = (() => {
    switch (state) {
      case 'before_open':
        return t('common.votesFrom17');
      case 'replay':
        return t('municipality.replay', { S: fmtSections(cols && cols.fraction > 0 ? cols.fraction : 0) });
      case 'loading':
        return t('municipality.loading');
      case 'error':
        return t('municipality.error');
      default:
        return null;
    }
  })();

  return (
    <PlaceCard kind="municipality" class="municipality-card" state={state}>
      <UpLink to={placeName(uf)} />
      <PlaceHeader
        title={name}
        sub={m ? t('municipality.sub', { SIGLA_UF: uf, E: fmtVoters(m.electorate_ref) }) : uf}
        seal={<StateSeal uf={uf} size={40} src={flagSrc(uf)} />}
        buttons={tv ? 0 : 1}
      />
      <UfNavButtons kind="municipality" />
      <section
        class="place-card__section municipality-card__president"
        data-testid="municipality-card-president"
        aria-labelledby={`${id}-president`}
      >
        <SectionLabel
          id={`${id}-president`}
          label={t('municipality.section')}
          actions={
            chip === 'counted' ? (
              <Chip tone="done" label={t('chip.counted')} />
            ) : chip === 'waiting' && (state === 'before_open' || state === 'no_sections') ? (
              <Chip label={t('chip.waiting')} />
            ) : undefined
          }
        />
        {numbers ? (
          <p class="place-note" data-testid="municipality-card-sections">
            {t('municipality.sections', { N: numbers.sectionsCounted, M: numbers.sections })}
          </p>
        ) : null}
        {note ? (
          <p class="place-note" data-testid="municipality-card-note">
            {note}
          </p>
        ) : null}
        {state === 'no_sections' ? <p class="place-note">{t('uf.noSections')}</p> : null}
        {showRows && pair ? (
          <div class="municipality-card__rows" data-testid="municipality-card-rows">
            {pair.map((c, i) => (
              <CandidateRow
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                share={shares[i as 0 | 1]}
                votes={votes ? votes[i as 0 | 1] : null}
                variant={votes ? 'bar-votes' : 'bar'}
                portraitSize={30}
                onOpen={interactive ? () => openProfile(c.number, 'president', 'list') : undefined}
              />
            ))}
          </div>
        ) : null}
        {showNames && pair ? (
          <div class="municipality-card__rows" data-testid="municipality-card-names">
            {pair.map((c) => (
              <CandidateRow
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                share={null}
                portraitSize={30}
                onOpen={interactive ? () => openProfile(c.number, 'president', 'list') : undefined}
              />
            ))}
          </div>
        ) : null}
        {ref && !tv ? (
          <p
            class="place-ref municipality-card__ref-2022 u-cq-780"
            data-testid="municipality-card-2022"
            title={t('compare.2022.title')}
          >
            <span class="place-ref__label">{t('compare.2022.label')}</span>
            {ref.items.map((it, i) => (
              <span
                key={it.number}
                class={`place-ref__item ${partyClass(partyOf(it.party_code))}`}
                data-number={it.number}
              >
                {i > 0 ? <span class="sr-only">, </span> : null}
                <span class="place-ref__swatch" data-dataviz="" aria-hidden="true" />
                {t('compare.2022.item', { NOME2022: it.name, P: pct1(it.share) })}
              </span>
            ))}
          </p>
        ) : null}
      </section>
      {state === 'votes' && numbers ? (
        <div class="place-card__section municipality-card__kpis u-cq-780" data-testid="municipality-card-kpis">
          {/* sem "Abstenção": a tela nunca a calcula por subtração (DECISOES item 14 e §28) */}
          <dl class="place-kpis">
            <KeyValueRow label={t('kpi.turnout')} value={numbers.turnoutRate} format={pct1} />
            <KeyValueRow label={t('kpi.blankNull')} value={numbers.blankNullRate} format={pct1} />
          </dl>
        </div>
      ) : null}
      {tv ? null : (
        <section
          class="place-card__section municipality-card__state u-cq-627"
          data-testid="municipality-card-state"
          aria-labelledby={hasRace ? `${id}-governor` : undefined}
        >
          {hasRace ? (
            <>
              <SectionLabel id={`${id}-governor`} label={t('uf.section.governor')} />
              <p class="place-note" data-testid="municipality-card-governor-note">
                {t('municipality.governorNote')}
              </p>
            </>
          ) : null}
          <Button
            class="municipality-card__see-state"
            label={t('municipality.seeState', { uf_de: ufOf(uf) })}
            onClick={() => openPlace({ kind: 'uf', uf })}
          />
        </section>
      )}
      {insights}
    </PlaceCard>
  );
}
